import React from "react";
import { Box, Chip, alpha, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

interface AddEntryDividerProps {
  onClick: () => void;
  disabled?: boolean;
  /** `data-tour` marker so the walkthrough can spotlight/reveal this divider. */
  dataTour?: string;
}

/**
 * Thin, near-invisible insert affordance shown between transcript entries.
 * Reveals an "Add entry" chip on hover so entries can be inserted in place
 * without the control taking up vertical space the rest of the time.
 */
export const AddEntryDivider: React.FC<AddEntryDividerProps> = ({
  onClick,
  disabled = false,
  dataTour,
}) => {
  const theme = useTheme();

  const handleActivate = () => {
    onClick();
  };

  return (
    <Box
      className="add-entry-divider"
      data-tour={dataTour}
      role="button"
      aria-label="Add entry"
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? undefined : 0}
      onClick={
        disabled
          ? undefined
          : (e) => {
              e.stopPropagation();
              handleActivate();
            }
      }
      onKeyDown={
        disabled
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                handleActivate();
              }
            }
      }
      sx={{
        position: "relative",
        height: 18,
        my: 0.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: 0,
        transition: "opacity 0.15s ease",
        outline: "none",
        "&:hover": disabled ? {} : { opacity: 1 },
        "&:focus-visible": disabled ? {} : { opacity: 1 },
      }}
    >
      {/* connecting line */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          height: "1px",
          transform: "translateY(-50%)",
          backgroundColor: alpha(theme.palette.primary.main, 0.4),
        }}
      />
      <Chip
        icon={<AddIcon sx={{ fontSize: 16 }} />}
        label="Add entry"
        size="small"
        color="primary"
        variant="outlined"
        sx={{
          height: 20,
          fontSize: "0.7rem",
          backgroundColor: theme.palette.background.paper,
          "& .MuiChip-icon": { ml: 0.5 },
        }}
      />
    </Box>
  );
};
